// Small UI kit for the admin panel: archive palette (parchment / ink / maroon), hairline borders, no shadows.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle, Calendar, Check, ChevronDown, ChevronLeft, ChevronRight, Loader2, Search, X
} from 'lucide-react';

export const cx = (...classes) => classes.filter(Boolean).join(' ');

export const FOCUS = 'focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-maroon/60';

// Closes a popover on outside click or Escape
export function useDismiss(open, onClose, ref) {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onClose();
    };
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, ref]);
}

// ── Typography ───────────────────────────────────────────────────────────────

export const Eyebrow = ({ children, className }) => (
  <p className={cx('font-mono text-[10px] uppercase tracking-[0.28em] text-umber/55', className)}>{children}</p>
);

export function PageHeader({ eyebrow, title, description, actions, back }) {
  return (
    <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
      <div className="min-w-0">
        {back}
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className="mt-2 font-serif text-[34px] md:text-[44px] leading-[1.05] tracking-[-0.01em] text-ink">{title}</h1>
        {description && <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-umber/70">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

// ── Buttons ──────────────────────────────────────────────────────────────────

const BUTTON_VARIANTS = {
  primary: 'bg-ink text-parchment border border-ink hover:bg-umber hover:border-umber',
  accent: 'bg-maroon text-parchment border border-maroon hover:bg-[#5a2424] hover:border-[#5a2424]',
  secondary: 'bg-vellum text-ink border border-umber/20 hover:border-umber/45',
  ghost: 'bg-transparent text-umber/70 border border-transparent hover:text-ink hover:bg-umber/[0.06]',
  danger: 'bg-transparent text-maroon border border-maroon/30 hover:bg-maroon hover:text-parchment hover:border-maroon',
};
const BUTTON_SIZES = {
  sm: 'h-8 px-3 text-[13px] gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-[15px] gap-2',
};

export function Button({ variant = 'secondary', size = 'md', icon: Icon, loading, className, children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      disabled={props.disabled || loading}
      className={cx(
        'inline-flex select-none items-center justify-center whitespace-nowrap rounded-lg font-medium transition-colors duration-150',
        'disabled:pointer-events-none disabled:opacity-40',
        FOCUS, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className,
      )}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : Icon ? <Icon className="h-4 w-4" strokeWidth={1.75} /> : null}
      {children}
    </button>
  );
}

export function IconButton({ icon: Icon, label, variant = 'ghost', size = 'md', className, ...props }) {
  const box = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      {...props}
      className={cx(
        'inline-flex items-center justify-center rounded-lg transition-colors duration-150 disabled:pointer-events-none disabled:opacity-40',
        FOCUS, BUTTON_VARIANTS[variant], box, className,
      )}
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} />
    </button>
  );
}

// ── Inputs ───────────────────────────────────────────────────────────────────

const FIELD = 'rounded-lg border bg-vellum transition-colors duration-150';

export function Field({ label, hint, children, className }) {
  return (
    <div className={className}>
      {label && <Eyebrow className="mb-2">{label}</Eyebrow>}
      {children}
      {hint && <p className="mt-1.5 text-xs text-umber/50">{hint}</p>}
    </div>
  );
}

export function TextInput({ icon: Icon, className, onClear, ...props }) {
  return (
    <div className={cx('group relative flex h-10 items-center', FIELD, 'border-umber/20 hover:border-umber/40 focus-within:border-ink focus-within:hover:border-ink', className)}>
      {Icon && <Icon className="pointer-events-none absolute left-3 h-4 w-4 text-umber/40 transition-colors group-focus-within:text-ink" strokeWidth={1.75} />}
      <input
        {...props}
        className={cx(
          'h-full w-full bg-transparent text-sm text-ink placeholder:text-umber/40 focus:outline-none',
          Icon ? 'pl-9' : 'pl-3', onClear && props.value ? 'pr-9' : 'pr-3',
        )}
      />
      {onClear && props.value ? (
        <button type="button" onClick={onClear} aria-label="Borrar"
          className="absolute right-2 rounded-md p-1 text-umber/45 transition-colors hover:bg-umber/[0.07] hover:text-ink">
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export const SearchInput = (props) => <TextInput icon={Search} {...props} />;

export function TextArea({ className, ...props }) {
  return (
    <textarea
      {...props}
      className={cx(
        'block w-full resize-none px-3 py-2.5 text-sm leading-relaxed text-ink placeholder:text-umber/40 focus:outline-none',
        FIELD, 'border-umber/20 hover:border-umber/40 focus:border-ink focus:hover:border-ink', className,
      )}
    />
  );
}

// Custom listbox: keyboard friendly, optional search, counts on the right
export function Select({
  value, onChange, options, placeholder = 'Seleccionar', searchable = false, icon: Icon, prefix,
  className, align = 'left', menuWidth = 'w-max min-w-full max-w-[360px]',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const listRef = useRef(null);
  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);
  useDismiss(open, close, ref);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => `${o.label} ${o.description || ''}`.toLowerCase().includes(q));
  }, [options, query]);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (open) setActive(Math.max(0, shown.findIndex((o) => o.value === value)));
  }, [open, shown, value]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const choose = (option) => {
    onChange(option.value);
    close();
  };

  const onKeyDown = (event) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key) && event.target.tagName !== 'INPUT') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, shown.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (shown[active]) choose(shown[active]);
    } else if (event.key === 'Tab') {
      close();
    }
  };

  return (
    <div ref={ref} className={cx('relative', className)} onKeyDown={onKeyDown}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cx(
          'flex h-10 w-full items-center gap-2 pl-3 pr-2.5 text-left text-sm', FIELD, FOCUS,
          open ? 'border-ink' : 'border-umber/20 hover:border-umber/40',
        )}
      >
        {Icon && <Icon className="h-4 w-4 flex-shrink-0 text-umber/40" strokeWidth={1.75} />}
        <span className="min-w-0 flex-1 truncate">
          {prefix && <span className="mr-1.5 text-umber/50">{prefix}</span>}
          <span className={selected && selected.value !== '' ? 'text-ink' : 'text-umber/50'}>
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <ChevronDown className={cx('h-4 w-4 flex-shrink-0 text-umber/40 transition-transform duration-150', open && 'rotate-180')} strokeWidth={1.75} />
      </button>

      {open && (
        <div className={cx('absolute z-40 mt-1.5 rounded-xl border border-umber/20 bg-vellum p-1', align === 'right' ? 'right-0' : 'left-0', menuWidth)}>
          {searchable && (
            <div className="mb-1 flex h-9 items-center gap-2 border-b border-umber/10 px-2.5">
              <Search className="h-3.5 w-3.5 text-umber/40" strokeWidth={1.75} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filtrar…"
                className="w-full bg-transparent text-sm text-ink placeholder:text-umber/40 focus:outline-none"
              />
            </div>
          )}
          <ul ref={listRef} role="listbox" className="max-h-72 overflow-y-auto overscroll-contain">
            {shown.length === 0 && <li className="px-3 py-6 text-center text-xs text-umber/45">Sin resultados</li>}
            {shown.map((option, index) => {
              const isSelected = option.value === value;
              return (
                <li
                  key={String(option.value)}
                  data-index={index}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActive(index)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(option)}
                  className={cx(
                    'flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm',
                    index === active && 'bg-umber/[0.07]',
                    isSelected ? 'text-ink' : 'text-umber/85',
                  )}
                >
                  <Check className={cx('h-3.5 w-3.5 flex-shrink-0 text-maroon', isSelected ? 'opacity-100' : 'opacity-0')} strokeWidth={2.25} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{option.label}</span>
                    {option.description && <span className="block truncate text-xs text-umber/50">{option.description}</span>}
                  </span>
                  {option.hint !== undefined && option.hint !== null && (
                    <span className="pl-3 font-mono text-[11px] tabular-nums text-umber/45">{option.hint}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Date range picker ────────────────────────────────────────────────────────

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const pad = (n) => String(n).padStart(2, '0');
export const toYmd = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const fromYmd = (text) => {
  const [y, m, d] = text.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const shortDate = (text, withYear) => fromYmd(text).toLocaleDateString('es-PE', {
  day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}),
});

function rangeLabel(from, to) {
  if (!from && !to) return null;
  if (from && to && from === to) return shortDate(from, true);
  if (from && to) return `${shortDate(from, from.slice(0, 4) !== to.slice(0, 4))} – ${shortDate(to, true)}`;
  return from ? `Desde ${shortDate(from, true)}` : `Hasta ${shortDate(to, true)}`;
}

function presets() {
  const today = new Date();
  const ago = (days) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return toYmd(d);
  };
  return [
    { label: 'Hoy', from: toYmd(today), to: toYmd(today) },
    { label: 'Últimos 7 días', from: ago(6), to: toYmd(today) },
    { label: 'Últimos 30 días', from: ago(29), to: toYmd(today) },
    { label: 'Este mes', from: toYmd(new Date(today.getFullYear(), today.getMonth(), 1)), to: toYmd(today) },
    { label: 'Este año', from: `${today.getFullYear()}-01-01`, to: toYmd(today) },
  ];
}

export function DateRangePicker({ from = '', to = '', onChange, className, placeholder = 'Cualquier fecha' }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from, to });
  const [hover, setHover] = useState('');
  const [view, setView] = useState(() => (from ? fromYmd(from) : new Date()));
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, ref);

  const toggle = () => {
    if (!open) {
      setDraft({ from, to });
      setView(from ? fromYmd(from) : new Date());
    }
    setOpen((o) => !o);
  };

  const commit = (next) => {
    onChange(next);
    setOpen(false);
  };

  const pick = (day) => {
    if (!draft.from || draft.to) {
      setDraft({ from: day, to: '' });
    } else if (day < draft.from) {
      setDraft({ from: day, to: '' });
    } else {
      commit({ from: draft.from, to: day });
    }
  };

  const year = view.getFullYear();
  const month = view.getMonth();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => toYmd(new Date(year, month, i + 1)))];
  const today = toYmd(new Date());
  const end = draft.to || (draft.from && hover && hover >= draft.from ? hover : '');
  const label = rangeLabel(from, to);

  return (
    <div ref={ref} className={cx('relative', className)}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={cx('flex h-10 w-full items-center gap-2 pl-3 pr-2.5 text-left text-sm', FIELD, FOCUS,
          open ? 'border-ink' : 'border-umber/20 hover:border-umber/40')}
      >
        <Calendar className="h-4 w-4 flex-shrink-0 text-umber/40" strokeWidth={1.75} />
        <span className={cx('min-w-0 flex-1 truncate', label ? 'text-ink' : 'text-umber/50')}>{label || placeholder}</span>
        {label ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Quitar fechas"
            onClick={(e) => {
              e.stopPropagation();
              onChange({ from: '', to: '' });
            }}
            className="rounded-md p-0.5 text-umber/45 hover:bg-umber/[0.07] hover:text-ink"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        ) : (
          <ChevronDown className={cx('h-4 w-4 text-umber/40 transition-transform', open && 'rotate-180')} strokeWidth={1.75} />
        )}
      </button>

      {open && (
        <div className="absolute left-0 z-40 mt-1.5 flex flex-col overflow-hidden rounded-xl border border-umber/20 bg-vellum sm:flex-row">
          <div className="flex flex-row flex-wrap gap-1 border-b border-umber/10 p-2 sm:w-40 sm:flex-col sm:flex-nowrap sm:border-b-0 sm:border-r">
            {presets().map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => commit({ from: preset.from, to: preset.to })}
                className={cx('rounded-lg px-2.5 py-1.5 text-left text-[13px] transition-colors', FOCUS,
                  preset.from === from && preset.to === to ? 'bg-ink text-parchment' : 'text-umber/80 hover:bg-umber/[0.07] hover:text-ink')}
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => commit({ from: '', to: '' })}
              className={cx('rounded-lg px-2.5 py-1.5 text-left text-[13px] text-umber/60 transition-colors hover:bg-umber/[0.07] hover:text-ink', FOCUS)}
            >
              Cualquier fecha
            </button>
          </div>

          <div className="w-[284px] p-3">
            <div className="mb-2 flex items-center justify-between">
              <IconButton icon={ChevronLeft} size="sm" label="Mes anterior" onClick={() => setView(new Date(year, month - 1, 1))} />
              <p className="font-serif text-lg capitalize text-ink">{MONTHS[month]} <span className="text-umber/50">{year}</span></p>
              <IconButton icon={ChevronRight} size="sm" label="Mes siguiente" onClick={() => setView(new Date(year, month + 1, 1))} />
            </div>
            <div className="grid grid-cols-7 text-center">
              {WEEKDAYS.map((d, i) => (
                <span key={i} className="py-1.5 font-mono text-[10px] uppercase tracking-widest text-umber/40">{d}</span>
              ))}
              {cells.map((day, i) => {
                if (!day) return <span key={`e${i}`} />;
                const isStart = day === draft.from;
                const isEnd = day === end;
                const inRange = draft.from && end && day > draft.from && day < end;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => pick(day)}
                    onMouseEnter={() => setHover(day)}
                    className={cx(
                      'h-9 text-[13px] tabular-nums transition-colors', FOCUS,
                      isStart || isEnd
                        ? 'rounded-lg bg-ink text-parchment'
                        : inRange
                          ? 'bg-umber/[0.08] text-ink hover:bg-umber/[0.14]'
                          : cx('rounded-lg hover:bg-umber/[0.1]', day === today ? 'font-semibold text-maroon' : 'text-umber/85'),
                    )}
                  >
                    {Number(day.slice(8))}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 border-t border-umber/10 pt-2 text-center text-xs text-umber/50">
              {draft.from && !draft.to ? 'Elige la fecha final' : 'Elige la fecha inicial'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Toggles ──────────────────────────────────────────────────────────────────

export function Segmented({ value, onChange, options, className, size = 'md' }) {
  return (
    <div role="radiogroup" className={cx('inline-flex items-center rounded-lg border border-umber/20 bg-vellum p-[3px]', size === 'sm' ? 'h-8' : 'h-10', className)}>
      {options.map((option) => {
        const active = value === option.value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={cx(
              'inline-flex h-full items-center gap-1.5 rounded-md px-3 text-[13px] transition-colors duration-150', FOCUS,
              active ? 'bg-ink text-parchment' : 'text-umber/65 hover:text-ink',
            )}
          >
            {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function Checkbox({ checked, onChange, label, description, disabled, className }) {
  return (
    <label className={cx('group flex cursor-pointer select-none items-start gap-3', disabled && 'pointer-events-none opacity-50', className)}>
      <input type="checkbox" className="peer sr-only" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span
        className={cx(
          'mt-[1px] flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-[5px] border transition-colors duration-150',
          'peer-focus-visible:outline peer-focus-visible:outline-1 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-maroon/60',
          checked ? 'border-ink bg-ink text-parchment' : 'border-umber/30 bg-vellum group-hover:border-umber/60',
        )}
      >
        {checked && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      {(label || description) && (
        <span className="text-sm leading-snug">
          <span className="text-ink">{label}</span>
          {description && <span className="mt-0.5 block text-xs text-umber/55">{description}</span>}
        </span>
      )}
    </label>
  );
}

export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="flex gap-8 overflow-x-auto border-b border-umber/15">
      {tabs.map(({ key, label, icon: Icon, count }) => {
        const active = key === value;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={cx(
              '-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b py-3.5 text-sm transition-colors duration-150', FOCUS,
              active ? 'border-ink text-ink' : 'border-transparent text-umber/55 hover:text-ink',
            )}
          >
            {Icon && <Icon className="h-4 w-4" strokeWidth={1.75} />}
            {label}
            {count !== undefined && count !== null && (
              <span className={cx('rounded-full px-1.5 py-px font-mono text-[10px] tabular-nums', active ? 'bg-ink text-parchment' : 'bg-umber/[0.08] text-umber/60')}>{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Display ──────────────────────────────────────────────────────────────────

const BADGE_TONES = {
  neutral: 'bg-umber/[0.07] text-umber/75',
  success: 'bg-[#4D5B3A]/[0.12] text-[#3B472C]',
  warning: 'bg-ochre/[0.12] text-[#8a4a20]',
  danger: 'bg-maroon/[0.1] text-maroon',
  dark: 'bg-ink text-parchment',
};

export function Badge({ tone = 'neutral', dot, children, className }) {
  return (
    <span className={cx('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium', BADGE_TONES[tone], className)}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />}
      {children}
    </span>
  );
}

export const Card = ({ className, children }) => (
  <div className={cx('rounded-2xl border border-umber/15 bg-vellum', className)}>{children}</div>
);

export function StatRow({ items }) {
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-umber/15 bg-umber/[0.12] md:grid-cols-5">
      {items.map((item, i) => (
        <div key={item.label} className={cx('bg-vellum px-5 py-4', i === items.length - 1 && items.length % 2 === 1 && 'col-span-2 md:col-span-1')}>
          <Eyebrow>{item.label}</Eyebrow>
          <p className="mt-2 font-serif text-[30px] leading-none lining-nums tabular-nums text-ink">{item.value}</p>
          {item.sub && <p className="mt-1.5 text-xs text-umber/50">{item.sub}</p>}
        </div>
      ))}
    </div>
  );
}

export function Progress({ value, total, tone = 'ink' }) {
  const percent = total ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-umber/10">
      <div className={cx('h-full rounded-full transition-all duration-500', tone === 'warning' ? 'bg-ochre' : 'bg-ink')} style={{ width: `${percent}%` }} />
    </div>
  );
}

export const Spinner = ({ className }) => <Loader2 className={cx('h-5 w-5 animate-spin text-umber/35', className)} />;

export function Notice({ tone = 'danger', children, onClose }) {
  const tones = {
    danger: 'border-maroon/25 bg-maroon/[0.05] text-maroon',
    success: 'border-[#4D5B3A]/25 bg-[#4D5B3A]/[0.06] text-[#3B472C]',
    neutral: 'border-umber/15 bg-umber/[0.04] text-umber/80',
  };
  return (
    <div className={cx('flex items-start gap-2.5 rounded-xl border px-4 py-3 text-sm', tones[tone])}>
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
      <span className="flex-1">{children}</span>
      {onClose && (
        <button type="button" onClick={onClose} aria-label="Cerrar" className="rounded p-0.5 opacity-60 hover:opacity-100">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, children, action }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-umber/20 px-6 py-20 text-center">
      {Icon && (
        <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-umber/15 text-umber/45">
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </span>
      )}
      <p className="font-serif text-2xl text-ink">{title}</p>
      {children && <p className="mt-2 max-w-sm text-sm leading-relaxed text-umber/60">{children}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Modal({ open = true, onClose, eyebrow, title, children, width = 'max-w-lg' }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[70] flex font-ui text-ink items-center justify-center bg-ink/45 p-4 backdrop-blur-[2px]" onMouseDown={onClose}>
      <div className={cx('w-full rounded-2xl border border-umber/20 bg-vellum', width)} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div>
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            <h3 className="mt-1.5 font-serif text-[28px] leading-tight text-ink">{title}</h3>
          </div>
          <IconButton icon={X} label="Cerrar" size="sm" onClick={onClose} />
        </div>
        <div className="px-6 pb-6 pt-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({ title, children, confirmLabel = 'Confirmar', tone = 'danger', onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal onClose={onClose} title={title} width="max-w-md">
      <p className="text-sm leading-relaxed text-umber/75">{children}</p>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant={tone === 'danger' ? 'accent' : 'primary'} loading={busy} onClick={run}>{confirmLabel}</Button>
      </div>
    </Modal>
  );
}
